import { isBoardsApiEnabled } from "./boardsApi";

export type GroupMeta = {
  id: string;
  title: string;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
};

export type ProjectMeta = {
  id: string;
  title: string;
  description: string;
  groupId?: string | null;
  createdAt: number;
  updatedAt: number;
};

export type ProjectFileMeta = {
  id: string;
  projectId: string;
  name: string;
  mimeType: string;
  size: number;
  kind: "document" | "image" | "other";
  key: string;
  createdAt: number;
  updatedAt: number;
};

export type ProjectDrawingMeta = {
  id: string;
  title: string;
  projectId?: string | null;
  createdAt: number;
  updatedAt: number;
};

type BoardsRuntimeConfig = {
  apiUrl?: string;
  apiToken?: string;
};

const getConfig = () => {
  const runtime = (window as Window & { __BOARDS_CONFIG__?: BoardsRuntimeConfig })
    .__BOARDS_CONFIG__ || {};
  const baseUrl = (
    runtime.apiUrl ||
    import.meta.env.VITE_APP_BOARDS_API_URL ||
    ""
  ).replace(/\/+$/, "");
  const token =
    runtime.apiToken || import.meta.env.VITE_APP_BOARDS_API_TOKEN || "";
  return { baseUrl, token, enabled: Boolean(baseUrl && token) };
};

export const isProjectsApiEnabled = () =>
  isBoardsApiEnabled() || getConfig().enabled;

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const { baseUrl, token, enabled } = getConfig();
  if (!enabled) {
    throw new Error("Projects API is not configured");
  }

  const headers = new Headers(init.headers || {});
  if (!headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (
    init.body &&
    !(init.body instanceof Blob) &&
    !(init.body instanceof ArrayBuffer) &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${baseUrl}${path}`, { ...init, headers });
  if (!response.ok) {
    const body = await response.text();
    throw new Error(
      `Projects API ${response.status}: ${body || response.statusText}`,
    );
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json() as Promise<T>;
  }
  return undefined as T;
};

export const projectsApi = {
  listGroups: async () => {
    const data = await request<{ groups: GroupMeta[] }>("/api/groups");
    return data.groups;
  },

  createGroup: async (title = "未命名分组") => {
    const data = await request<{ group: GroupMeta }>("/api/groups", {
      method: "POST",
      body: JSON.stringify({ title }),
    });
    return data.group;
  },

  updateGroup: async (
    id: string,
    payload: { title?: string; sortOrder?: number },
  ) => {
    const data = await request<{ group: GroupMeta }>(
      `/api/groups/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
    return data.group;
  },

  removeGroup: (id: string) =>
    request<{ ok: true }>(`/api/groups/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  list: async () => {
    const data = await request<{ projects: ProjectMeta[] }>("/api/projects");
    return data.projects;
  },

  create: async (
    payload: {
      title?: string;
      description?: string;
      groupId?: string | null;
    } = {},
  ) => {
    const data = await request<{ project: ProjectMeta }>("/api/projects", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return data.project;
  },

  get: async (id: string) => {
    const data = await request<{ project: ProjectMeta }>(
      `/api/projects/${encodeURIComponent(id)}`,
    );
    return data.project;
  },

  update: async (
    id: string,
    payload: {
      title?: string;
      description?: string;
      groupId?: string | null;
    },
  ) => {
    const data = await request<{ project: ProjectMeta }>(
      `/api/projects/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
    return data.project;
  },

  remove: (id: string) =>
    request<{ ok: true }>(`/api/projects/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  listDrawings: async (projectId: string) => {
    const data = await request<{ drawings: ProjectDrawingMeta[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/drawings`,
    );
    return data.drawings;
  },

  createDrawing: async (projectId: string, title = "未命名画板") => {
    const data = await request<{ drawing: ProjectDrawingMeta }>(
      "/api/drawings",
      {
        method: "POST",
        body: JSON.stringify({ title, projectId }),
      },
    );
    return data.drawing;
  },

  listFiles: async (projectId: string) => {
    const data = await request<{ files: ProjectFileMeta[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/files`,
    );
    return data.files;
  },

  uploadFile: async (projectId: string, file: File) => {
    const { baseUrl, token, enabled } = getConfig();
    if (!enabled) {
      throw new Error("Projects API is not configured");
    }
    const response = await fetch(
      `${baseUrl}/api/projects/${encodeURIComponent(projectId)}/files`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": file.type || "application/octet-stream",
          "X-File-Name": encodeURIComponent(file.name),
        },
        body: file,
      },
    );
    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Projects API ${response.status}: ${body || response.statusText}`,
      );
    }
    const data = (await response.json()) as { file: ProjectFileMeta };
    return data.file;
  },

  downloadUrl: (projectId: string, fileId: string) => {
    const { baseUrl } = getConfig();
    return `${baseUrl}/api/projects/${encodeURIComponent(
      projectId,
    )}/files/${encodeURIComponent(fileId)}`;
  },

  fetchFileBlob: async (projectId: string, fileId: string) => {
    const { baseUrl, token, enabled } = getConfig();
    if (!enabled) {
      throw new Error("Projects API is not configured");
    }
    const response = await fetch(
      `${baseUrl}/api/projects/${encodeURIComponent(
        projectId,
      )}/files/${encodeURIComponent(fileId)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    return response.blob();
  },

  removeFile: (projectId: string, fileId: string) =>
    request<{ ok: true }>(
      `/api/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(
        fileId,
      )}`,
      { method: "DELETE" },
    ),
};
